import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Button, Input, Toaster } from '../../components/common';
import { useAuth } from '../../contexts/AuthContext';
import { routes } from '../../lib/routes';
import { passwordErrorMessages } from '../../lib/constants.tsx';
import { getPasswordStrength } from '../../utils/general.ts';
import { MdDone, MdEast } from 'react-icons/md';
import toast from 'react-hot-toast';
import type { ApiResponse } from '../../utils';

interface ResetPasswordFormData {
  password: string;
  confirmPassword: string;
}

const ResetPasswordForm = () => {
  const navigate = useNavigate();

  const { resetPassword, error, isLoading } = useAuth();
  const {
    register,
    watch,
    setError,
    clearErrors,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordFormData>({
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  });

  const disabledSubmit =
    watch('password') === '' || watch('confirmPassword') === '';

  const onSubmit = async (data: ResetPasswordFormData) => {
    const { password, confirmPassword } = data;

    if (password !== confirmPassword) {
      toast.custom(t => (
        <Toaster
          message='Passwords do not match, Please try again.'
          type='error'
          id={t.id}
        />
      ));

      setError('password', { message: '' });
      setError('confirmPassword', { message: '' });

      return;
    }

    const payload = {
      password,
    };

    try {
      await resetPassword(payload);
    } catch (error) {
      toast.custom(t => (
        <Toaster
          message={(error as ApiResponse)?.message as string}
          type='error'
          id={t.id}
        />
      ));
      navigate(routes.forgotPassword);
      console.error('Login failed:', error);
    }
  };

  return (
    <form className='w-full' onSubmit={handleSubmit(onSubmit)}>
      {error && (
        <div className='rounded-md bg-red-50 p-4 mb-3'>
          <div className='text-sm text-red-700'>{error}</div>
        </div>
      )}

      <div className='w-full mb-4'>
        <Input
          label='New Password'
          type='password'
          autoComplete='current-password'
          placeholder='Enter new password'
          required
          color={errors.password ? 'error' : 'default'}
          {...register('password', {
            required: 'Password is required',
            minLength: {
              value: 8,
              message: passwordErrorMessages.CHARACTERS,
            },
            pattern: {
              value: /^(?=.*[0-9])(?=.*[^A-Za-z0-9]).+$/,
              message: passwordErrorMessages.ALPHANUMERIC,
            },
            validate: value => {
              if (getPasswordStrength(value) === 'Weak') {
                return passwordErrorMessages.WEAK;
              }
            },
            onChange: () => {
              clearErrors('password');
              clearErrors('confirmPassword');
            },
          })}
        />

        {watch('password') && (
          <div className='text-xs text-gray-500 mt-2'>
            <p
              className={`flex items-center gap-2 mb-2 font-semibold ${getPasswordStrength(watch('password')) === 'Strong' ? 'text-green-600' : getPasswordStrength(watch('password')) === 'Medium' ? 'text-yellow-600' : 'text-red-500'}`}
            >
              <MdDone size={16} /> Password strength:{' '}
              {getPasswordStrength(watch('password'))}
            </p>
            <p
              className={`flex items-center gap-2 mb-2 font-semibold ${errors.password?.message === passwordErrorMessages.CONTAIN_NAME ? 'text-red-500' : 'text-green-600'}`}
            >
              <MdDone size={16} /> Cannot contain your name or email address
            </p>
            <p
              className={`flex items-center gap-2 mb-2 font-semibold ${errors.password?.message === passwordErrorMessages.CHARACTERS ? 'text-red-500' : 'text-green-600'}`}
            >
              <MdDone size={16} /> At least 8 characters
            </p>
            <p
              className={`flex items-center gap-2 mb-0 font-semibold ${errors.password?.message === passwordErrorMessages.ALPHANUMERIC ? 'text-red-500' : 'text-green-600'}`}
            >
              <MdDone size={16} /> Contains a number or symbol
            </p>
          </div>
        )}
      </div>

      <div className='w-full mb-6'>
        <Input
          label='Confirm Password'
          type='password'
          autoComplete='confirm-password'
          placeholder='Enter confirm password'
          required
          color={errors.confirmPassword ? 'error' : 'default'}
          helperText={errors.confirmPassword?.message as string}
          {...register('confirmPassword', {
            required: 'Confirm password is required',
            onChange: () => {
              clearErrors('password');
              clearErrors('confirmPassword');
            },
          })}
        />
      </div>

      <Button
        type='submit'
        isLoading={isLoading}
        disabled={disabledSubmit}
        className='group relative w-full'
      >
        Submit <MdEast size={24} />
      </Button>
    </form>
  );
};

export default ResetPasswordForm;
